import { useEffect, useState, useMemo } from "react";
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
} from "reactflow";
import {
  Paper,
  Text,
  Stack,
  Group,
  Loader,
  Center,
  Alert,
  Box,
  useMantineTheme,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBrain,
  IconClock,
  IconCoin,
  IconTool,
} from "@tabler/icons-react";
import { getAgentTreeForLog } from "../api";
import { nodeTypes } from "./AgentExecutionGraphNodes";
import {
  transformTreeToReactFlow,
  getLayoutedElements,
  calculateTreeStats,
} from "./AgentExecutionGraphUtils";

import "reactflow/dist/style.css";

/**
 * Agent Execution Graph visualization component.
 * Displays the hierarchical execution of multi-agent flows as an interactive graph.
 */
export default function AgentExecutionGraph({ requestId }) {
  const theme = useMantineTheme();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Fetch the agent tree data
  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getAgentTreeForLog(requestId)
      .then((data) => {
        setTree(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch agent tree:", err);
        setError(err.message || "Failed to load agent execution graph");
        setLoading(false);
      });
  }, [requestId]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!tree) return null;
    return calculateTreeStats(tree);
  }, [tree]);

  // Transform tree to ReactFlow format
  useEffect(() => {
    if (!tree) return;

    const { nodes: rfNodes, edges: rfEdges } = transformTreeToReactFlow(
      tree,
      requestId
    );

    // Apply layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      rfNodes,
      rfEdges
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [tree, requestId, setNodes, setEdges]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        {error}
      </Alert>
    );
  }

  if (!tree) {
    return (
      <Alert color="gray" icon={<IconBrain size={16} />}>
        No agent execution data available for this request.
      </Alert>
    );
  }

  // Single node (no children) - show simple message
  if (!tree.children || tree.children.length === 0) {
    return (
      <Alert color="gray" icon={<IconBrain size={16} />}>
        This request has no sub-agent invocations. The execution graph shows
        multi-agent hierarchies when agents spawn other agents.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Summary stats */}
      <Group gap="lg">
        <Group gap={4}>
          <IconBrain size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Agents:{" "}
            <Text span fw={600}>
              {stats?.agentCount}
            </Text>
          </Text>
        </Group>
        <Group gap={4}>
          <IconTool size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Tool Calls:{" "}
            <Text span fw={600}>
              {stats?.toolCount}
            </Text>
          </Text>
        </Group>
        <Group gap={4}>
          <IconClock size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Duration:{" "}
            <Text span fw={600}>
              {stats?.totalDuration < 1000
                ? `${stats?.totalDuration}ms`
                : `${(stats?.totalDuration / 1000).toFixed(2)}s`}
            </Text>
          </Text>
        </Group>
        <Group gap={4}>
          <IconCoin size={14} color="orange" />
          <Text size="sm" c="dimmed">
            Total Cost:{" "}
            <Text span fw={600}>
              {(stats?.totalCredits / 10000).toFixed(4)}c
            </Text>
          </Text>
        </Group>
        <Group gap={4}>
          <IconArrowRight size={14} color="gray" />
          <Text size="sm" c="dimmed">
            Max Depth:{" "}
            <Text span fw={600}>
              {stats?.maxDepth}
            </Text>
          </Text>
        </Group>
      </Group>

      {/* Graph visualization */}
      <Paper withBorder radius="sm" style={{ height: 500 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
          <Background color="#e9ecef" gap={16} />
          <MiniMap
            nodeColor={(node) => {
              if (node.data?.status === "failed") return theme.colors.red[5];
              if (node.data?.isRoot) return theme.colors.blue[5];
              return theme.colors.indigo[4];
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
            style={{ backgroundColor: "#f8f9fa" }}
          />
        </ReactFlow>
      </Paper>

      {/* Legend */}
      <Group gap="md">
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: theme.colors.blue[0],
              border: `2px solid ${theme.colors.blue[5]}`,
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            Root Agent
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: theme.colors.indigo[0],
              border: `1px solid ${theme.colors.indigo[4]}`,
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            Child Agent
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: theme.colors.green[0],
              border: `2px solid ${theme.colors.green[5]}`,
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            Selected Request
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: theme.colors.red[0],
              border: `2px solid ${theme.colors.red[5]}`,
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            Failed
          </Text>
        </Group>
        <Group gap={4}>
          <Box
            style={{
              width: 20,
              height: 2,
              backgroundColor: "#fd7e14",
            }}
          />
          <Text size="xs" c="dimmed">
            Agent Spawn
          </Text>
        </Group>
        <Group gap={4}>
          <IconTool size={12} color="gray" />
          <Text size="xs" c="dimmed">
            Tool Call
          </Text>
        </Group>
      </Group>
    </Stack>
  );
}
